
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsx mdx */
import { Paragraph, Playground } from '@ecl/website-components';
import Example234612 from '@ecl/eu-react-layout-grid/examples/234612';
import Example642 from '@ecl/eu-react-layout-grid/examples/642';
import Example282 from '@ecl/eu-react-layout-grid/examples/282';
import Example336 from '@ecl/eu-react-layout-grid/examples/336';
import Offset from '@ecl/eu-react-layout-grid/examples/Offset';

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
  return <div {...props}/>
};

const layoutProps = {
  
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Paragraph size="lead" mdxType="Paragraph">
  <strong>
    The ECL uses an abridged version of the Bootstrap 12 column grid
  </strong>{' '}
  as the basic underlying structure for all web pages. The grid is composed of a
  set of columns, rows and containers used for aligning the content as well as
  the general layout.
    </Paragraph>
    <h2>{`The grid:`}</h2>
    <ul>
      <li parentName="ul">{`Containers are required when using our default grid system as they are the most basic layout elements, representing a means to horizontally align components or content elements - this is done by invoking `}<inlineCode parentName="li">{`.ecl-container`}</inlineCode></li>
      <li parentName="ul">{`The column classes `}<strong parentName="li">{`express the number of columns to be engaged out of the maximum`}</strong>{`, which is 12 per row (e.g. an element invoking `}<inlineCode parentName="li">{`.ecl-col-2`}</inlineCode>{` occupies two columns)`}</li>
      <li parentName="ul"><strong parentName="li">{`Container widths and breakpoints are expressed in pixels`}</strong></li>
      <li parentName="ul">{`Each column has `}<strong parentName="li">{`horizontal padding (gutter) for regulating the space between`}</strong>{` them`}</li>
      <li parentName="ul"><strong parentName="li">{`The gutters are expressed in rems`}</strong></li>
      <li parentName="ul"><strong parentName="li">{`The width of the columns is always proportional to the container width`}</strong>{`, which is fixed (except for viewports <480px), and they are expressed in percentages`}</li>
      <li parentName="ul">{`The rows wrap around the columns, allowing you to specify the amount of columns content wraps around`}</li>
    </ul>
    <h2>{`ECL Grid Options`}</h2>
    <p>{`The grid's breakpoints are established on the minimum width media queries, therefore the are applied on the targeted breakpoint and all those above it.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`viewport`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Extra small (≥ 0px)`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Small (≥ 480px)`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Medium (≥ 768px)`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Large (≥ 996px)`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Extra large (≥ 1140px)`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`container width`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`100%`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`480px`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`768px`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`996px`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`1140px`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`class prefix`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{` `}<inlineCode parentName="td">{`.ecl-col-`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`.ecl-col-sm-`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`.ecl-col-md-`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`.ecl-col-lg-`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`.ecl-col-xl-`}</inlineCode></td>
        </tr>
      </tbody>
    </table>
    <h2>{`ECL Grid Examples`}</h2>
    <Playground system="eu" selectedKind="layout-grid" selectedStory="2-3-4-6-12" showFrame mdxType="Playground">
  <Example234612 mdxType="Example234612" />
    </Playground>
    <Playground system="eu" selectedKind="layout-grid" selectedStory="6-4-2" showFrame mdxType="Playground">
  <Example642 mdxType="Example642" />
    </Playground>
    <Playground system="eu" selectedKind="layout-grid" selectedStory="2-8-2" showFrame mdxType="Playground">
  <Example282 mdxType="Example282" />
    </Playground>
    <Playground system="eu" selectedKind="layout-grid" selectedStory="3-3-6" showFrame mdxType="Playground">
  <Example336 mdxType="Example336" />
    </Playground>
    <h2>{`Grid offset`}</h2>
    <p>{`When using the ECL grid, there is the ability to offset columns. You can offset columns from left to right by invoking the `}<inlineCode parentName="p">{`.ecl-offset-*`}</inlineCode>{` classes (where `}{`*`}{` = the number of columns). These classes simply increase the left margin by `}{`*`}{` number of columns.`}</p>
    <Playground system="eu" selectedKind="layout-grid" selectedStory="offset" showFrame mdxType="Playground">
  <Offset mdxType="Offset" />
    </Playground>
    <h2>{`Do's`}</h2>
    <ul>
      <li parentName="ul">{`always place content within columns`}</li>
      <li parentName="ul">{`specify the exact amount of columns content must occupy`}</li>
      <li parentName="ul">{`use `}<strong parentName="li"><inlineCode parentName="strong">{`.ecl-offset-*`}</inlineCode>{` grid classes`}</strong>{` for `}<strong parentName="li">{`offsetting content`}</strong></li>
    </ul>
    <h2>{`Don'ts`}</h2>
    <ul>
      <li parentName="ul">{`don't use margin to offset content`}</li>
    </ul>
    </MDXLayout>;
}

;
MDXContent.isMDXComponent = true;